import { Dialog, Divider } from '@mui/material';
import { OptionallyVisible } from 'components';
import { FieldArray, Form, Formik, getIn } from 'formik';
import { PropsWithChildren, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BridgeKycInfoData } from 'services/merchants/types';

import { Button } from 'components/buttons';
import { Panel } from 'components/containers';
import { FormField } from 'components/form/formField/formField';
import IconAdd from 'components/icons/add';
import IconRemove from 'components/icons/dash';
import { InputType } from 'components/inputs';
import { SelectItem } from 'components/inputs/select/select';
import LoadingIndicator from 'components/loadingIndicator/loadingIndicator';

import { Response, SuccessResponse } from 'types/http';

import { BridgeKycInfoProvider } from '../../../kyb/BridgeKycInfoContext';
import Address from '../../../kyb/components/address/address';
import Documents from '../../../kyb/components/documents/documents';
import { PersonFieldStatusBadge } from '../../../kyb/components/fieldStatusBadge/FieldStatusBadge';
import FormikCalendar from '../../../kyb/components/formikCalendar/formikCalendar';
import FormikInput from '../../../kyb/components/formikTextInput/formikTextInput';
import IdentifyingInformation from '../../../kyb/components/identifyingInformation/identifyingInformation';
import { FormControls } from '../../../kyb/components/modal/components/formControls/formControls';
import { CustomTimePicker } from '../../../kyb/components/step1/components/customTimePicker/customTimePicker';
import FormikSwitch from '../../../kyb/components/switch/switch';
import { Section, SectionRow } from '../../../kyb/styles';
import { stripFilesRecursive } from '../../../utils/stripFiles';
import {
  ASSOCIATED_PERSONS_INITIAL_VALUES,
  ASSOCIATED_PERSON_INITIAL_VALUES,
  AssociatedPersonFormType,
  AssociatedPersonsFormType,
  FORM_FIELDS,
  LABELS,
  createAssociatedPersonsSchema,
} from '../../keys';
import { UseAssociatedPersonSessionFormResult } from '../../useSessionForm';
import DeleteConfirmationModal from './deleteConfirmationModal/deleteConfirmationModal';
import {
  BeneficiaryActions,
  BeneficiaryHeader,
  BeneficiarySection,
  BeneficiaryTitle,
  Buttons,
  Delimeter,
  ModalRoot,
  PanelContent,
  RecordButton,
} from './styles';

interface AssociatedPersonModalProps {
  merchantUuid?: string;
  bridgeKycInfo?: BridgeKycInfoData | null;
  onConfirm: (payload: AssociatedPersonsFormType) => Promise<void>;
  onDeletePerson: (personUuid: string) => Promise<{ success: boolean }>;
  isOpen: boolean;
  closeModal: () => void;
  countries: SelectItem[];
  error?: Record<string, string>;
  isSubmitting: boolean;
  isDeleting?: boolean;
  isLoading?: boolean;
  submitResponse?: Response<SuccessResponse>;
  clearBackendErrors?: () => void;
  sessionControllers: UseAssociatedPersonSessionFormResult;
  titanProps: AssociatedTitanProps;
}

interface DeleteConfirmationState {
  isOpen: boolean;
  beneficiaryIndex: number | null;
  beneficiary: AssociatedPersonFormType | null;
  removeFromForm: ((index: number) => void) | null;
}

interface FormikValuesObserverProps {
  values: AssociatedPersonsFormType;
  resetForm: (props: { values: AssociatedPersonsFormType }) => void;
}

interface AssociatedTitanProps {
  titanFormData: AssociatedPersonsFormType | undefined;
  isLoadingTitanData: boolean;
  fetchTitanData: () => Promise<void>;
  onInsertTitanData: () => void;
}

export const AssociatedPersonModal = ({
  merchantUuid,
  bridgeKycInfo,
  titanProps,
  onConfirm,
  onDeletePerson,
  isOpen,
  closeModal,
  countries,
  error,
  isSubmitting,
  isDeleting = false,
  isLoading,
  submitResponse,
  clearBackendErrors,
  sessionControllers,
}: PropsWithChildren<AssociatedPersonModalProps>) => {
  const { t } = useTranslation();
  const lastHandledRequestId = useRef<string | null>(null);
  const { titanFormData, isLoadingTitanData, onInsertTitanData } = titanProps;

  const [deleteConfirmation, setDeleteConfirmation] = useState<DeleteConfirmationState>({
    isOpen: false,
    beneficiaryIndex: null,
    beneficiary: null,
    removeFromForm: null,
  });

  const validationSchema = useMemo(() => {
    return createAssociatedPersonsSchema(t(LABELS.FORM.FIELD_REQUIRED));
  }, [t]);

  const isSuccess = submitResponse?.data?.success;
  const hasErrors = error ? Object.keys(error).length > 0 : false;
  const currentRequestId = submitResponse?.requestId;

  const { sessionValues, handleFormValueChange, setFormValues, resetSessionForm, refreshSessionValues } = sessionControllers;

  const handleDeleteClick = useCallback(
    (beneficiaryIndex: number, beneficiary: AssociatedPersonFormType, removeFromForm: (index: number) => void) => {
      setDeleteConfirmation({
        isOpen: true,
        beneficiaryIndex,
        beneficiary,
        removeFromForm,
      });
    },
    [],
  );

  const handleDeleteCancel = useCallback(() => {
    setDeleteConfirmation({
      isOpen: false,
      beneficiaryIndex: null,
      beneficiary: null,
      removeFromForm: null,
    });
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    const { beneficiary, beneficiaryIndex, removeFromForm } = deleteConfirmation;

    if (beneficiaryIndex === null || !removeFromForm) return;

    if (beneficiary?.uuid) {
      const result = await onDeletePerson(beneficiary.uuid);
      if (result.success) {
        removeFromForm(beneficiaryIndex);
        let updatedBeneficiaries = sessionValues.beneficiaries.filter((_, idx) => idx !== beneficiaryIndex);
        if (updatedBeneficiaries.length === 0) {
          updatedBeneficiaries = [{ ...ASSOCIATED_PERSON_INITIAL_VALUES }];
        }
        setFormValues({ ...sessionValues, beneficiaries: updatedBeneficiaries });
      }
    } else {
      removeFromForm(beneficiaryIndex);
      let updatedBeneficiaries = sessionValues.beneficiaries.filter((_, idx) => idx !== beneficiaryIndex);
      if (updatedBeneficiaries.length === 0) {
        updatedBeneficiaries = [{ ...ASSOCIATED_PERSON_INITIAL_VALUES }];
      }
      setFormValues({ ...sessionValues, beneficiaries: updatedBeneficiaries });
    }

    handleDeleteCancel();
  }, [deleteConfirmation, onDeletePerson, sessionValues, setFormValues, handleDeleteCancel]);

  useEffect(() => {
    if (isSuccess && !hasErrors && currentRequestId && currentRequestId !== lastHandledRequestId.current) {
      lastHandledRequestId.current = currentRequestId;
      resetSessionForm();
      closeModal();
    }
  }, [submitResponse, error, isSuccess, hasErrors]);

  useEffect(() => {
    if (isOpen) {
      refreshSessionValues();
    }
  }, [isOpen, refreshSessionValues]);

  useEffect(() => {
    if (merchantUuid && sessionValues.uuid !== merchantUuid) {
      resetSessionForm();
    }
  }, [merchantUuid]);

  const handleSubmit = async (values: AssociatedPersonsFormType) => {
    await onConfirm(values);
  };

  const FormikValuesObserver = ({ values, resetForm }: FormikValuesObserverProps) => {
    const lastSerialized = useRef<string>(JSON.stringify(stripFilesRecursive(values)));
    const lastSessionSerialized = useRef<string>(JSON.stringify(sessionValues));
    const isResetting = useRef<boolean>(false);

    useEffect(() => {
      if (isResetting.current) {
        isResetting.current = false;
        return;
      }

      const newSerialized = JSON.stringify(stripFilesRecursive(values));
      if (lastSerialized.current !== newSerialized) {
        handleFormValueChange(values);
        lastSerialized.current = newSerialized;
      }
    }, [values]);

    useEffect(() => {
      const sessionSerialized = JSON.stringify(sessionValues);
      if (lastSessionSerialized.current !== sessionSerialized) {
        const currentSerialized = JSON.stringify(stripFilesRecursive(values));
        if (currentSerialized !== sessionSerialized) {
          isResetting.current = true;
          resetForm({ values: sessionValues });
          lastSerialized.current = sessionSerialized;
        }
        lastSessionSerialized.current = sessionSerialized;
      }
    }, [sessionValues]);

    return null;
  };

  const renderBeneficiaryForm = (
    beneficiaryIndex: number,
    beneficiary: AssociatedPersonFormType,
    beneficiariesCount: number,
    removeBeneficiary: (index: number) => void,
    errors: Record<string, unknown>,
  ) => {
    const fieldPrefix = `beneficiaries[${beneficiaryIndex}]`;
    const ownershipError = getIn(errors, `${fieldPrefix}.ownershipValidation`);
    const isNotLastBeneficiary = beneficiaryIndex !== beneficiariesCount - 1;

    return (
      <BeneficiarySection key={beneficiaryIndex}>
        <BeneficiaryHeader>
          <BeneficiaryTitle>
            {t(LABELS.BENEFICIARY.TITLE)} #{beneficiaryIndex + 1}
          </BeneficiaryTitle>
        </BeneficiaryHeader>

        <SectionRow>
          <Section>
            <CustomTimePicker
              fieldPath={`${fieldPrefix}.${FORM_FIELDS.KYC_SCREEN}.screenedAt`}
              error={error}
              label={t(LABELS.FORM.KYC_SCREEN.TITLE)}
            />
          </Section>
          {beneficiary.uuid && <PersonFieldStatusBadge fieldName={FORM_FIELDS.KYC_SCREEN} personUuid={beneficiary.uuid} />}
        </SectionRow>

        <SectionRow>
          <Section>
            <CustomTimePicker
              fieldPath={`${fieldPrefix}.${FORM_FIELDS.OFAC_SCREEN}.screenedAt`}
              error={error}
              label={t(LABELS.FORM.OFAC_SCREEN.TITLE)}
            />
          </Section>
          {beneficiary.uuid && <PersonFieldStatusBadge fieldName={FORM_FIELDS.OFAC_SCREEN} personUuid={beneficiary.uuid} />}
        </SectionRow>

        <SectionRow>
          <Section>
            <FormikInput
              label={t(LABELS.FORM.FIRST_NAME.TITLE)}
              fieldPath={`${fieldPrefix}.${FORM_FIELDS.FIRST_NAME}`}
              placeholder={t(LABELS.FORM.FIRST_NAME.PLACEHOLDER)}
              error={error}
            />
          </Section>
          {beneficiary.uuid && <PersonFieldStatusBadge fieldName={FORM_FIELDS.FIRST_NAME} personUuid={beneficiary.uuid} />}
        </SectionRow>

        <SectionRow>
          <Section>
            <FormikInput
              label={t(LABELS.FORM.LAST_NAME.TITLE)}
              fieldPath={`${fieldPrefix}.${FORM_FIELDS.LAST_NAME}`}
              placeholder={t(LABELS.FORM.LAST_NAME.PLACEHOLDER)}
              error={error}
            />
          </Section>
          {beneficiary.uuid && <PersonFieldStatusBadge fieldName={FORM_FIELDS.LAST_NAME} personUuid={beneficiary.uuid} />}
        </SectionRow>

        <SectionRow>
          <Section>
            <FormikInput
              label={t(LABELS.FORM.EMAIL.TITLE)}
              fieldPath={`${fieldPrefix}.${FORM_FIELDS.EMAIL}`}
              placeholder={t(LABELS.FORM.EMAIL.PLACEHOLDER)}
              error={error}
            />
          </Section>
          {beneficiary.uuid && <PersonFieldStatusBadge fieldName={FORM_FIELDS.EMAIL} personUuid={beneficiary.uuid} />}
        </SectionRow>

        <SectionRow>
          <Section>
            <FormikCalendar
              placeholder={t(LABELS.FORM.BIRTH_DATE.PLACEHOLDER)}
              fieldPath={`${fieldPrefix}.${FORM_FIELDS.BIRTH_DATE}`}
              label={t(LABELS.FORM.BIRTH_DATE.TITLE)}
            />
          </Section>
          {beneficiary.uuid && <PersonFieldStatusBadge fieldName={FORM_FIELDS.BIRTH_DATE} personUuid={beneficiary.uuid} />}
        </SectionRow>

        <Section>
          <FormikInput
            label={t(LABELS.FORM.PHONE.TITLE)}
            fieldPath={`${fieldPrefix}.${FORM_FIELDS.PHONE}`}
            placeholder={t(LABELS.FORM.PHONE.PLACEHOLDER)}
            error={error}
          />
        </Section>

        <SectionRow>
          <Section>
            <Address
              title={LABELS.FORM.ADDRESS.TITLE}
              fieldPath={`${fieldPrefix}.${FORM_FIELDS.RESIDENTIAL_ADDRESS}`}
              countries={countries}
              error={error}
              isPartialMode={false}
            />
          </Section>
          {beneficiary.uuid && <PersonFieldStatusBadge fieldName={FORM_FIELDS.RESIDENTIAL_ADDRESS} personUuid={beneficiary.uuid} />}
        </SectionRow>

        <FormField error={ownershipError}>
          <SectionRow>
            <FormikSwitch label={t(LABELS.FORM.HAS_OWNERSHIP.LABEL)} fieldPath={`${fieldPrefix}.${FORM_FIELDS.HAS_OWNERSHIP}`} />
            {beneficiary.uuid && <PersonFieldStatusBadge fieldName={FORM_FIELDS.HAS_OWNERSHIP} personUuid={beneficiary.uuid} />}
          </SectionRow>
          <SectionRow>
            <FormikSwitch label={t(LABELS.FORM.HAS_CONTROL.LABEL)} fieldPath={`${fieldPrefix}.${FORM_FIELDS.HAS_CONTROL}`} />
            {beneficiary.uuid && <PersonFieldStatusBadge fieldName={FORM_FIELDS.HAS_CONTROL} personUuid={beneficiary.uuid} />}
          </SectionRow>
          <SectionRow>
            <FormikSwitch label={t(LABELS.FORM.IS_SIGNER.LABEL)} fieldPath={`${fieldPrefix}.${FORM_FIELDS.IS_SIGNER}`} />
            {beneficiary.uuid && <PersonFieldStatusBadge fieldName={FORM_FIELDS.IS_SIGNER} personUuid={beneficiary.uuid} />}
          </SectionRow>
        </FormField>

        <FormikSwitch label={t(LABELS.FORM.IS_DIRECTOR.LABEL)} fieldPath={`${fieldPrefix}.${FORM_FIELDS.IS_DIRECTOR}`} />

        <Section>
          <FormikInput
            label={t(LABELS.FORM.TITLE_FIELD.TITLE)}
            fieldPath={`${fieldPrefix}.${FORM_FIELDS.TITLE}`}
            placeholder={t(LABELS.FORM.TITLE_FIELD.PLACEHOLDER)}
            error={error}
          />

          <FormikInput
            type={InputType.Number}
            label={t(LABELS.FORM.OWNERSHIP_PERCENTAGE.TITLE)}
            fieldPath={`${fieldPrefix}.${FORM_FIELDS.OWNERSHIP_PERCENTAGE}`}
            placeholder={t(LABELS.FORM.OWNERSHIP_PERCENTAGE.PLACEHOLDER)}
            error={error}
          />

          <FormikCalendar
            placeholder={t(LABELS.FORM.RELATIONSHIP_AT.PLACEHOLDER)}
            fieldPath={`${fieldPrefix}.${FORM_FIELDS.RELATIONSHIP_ESTABLISHED_AT}`}
            label={t(LABELS.FORM.RELATIONSHIP_AT.TITLE)}
          />
        </Section>

        <FieldArray
          render={({ insert, remove }) => (
            <IdentifyingInformation
              error={error}
              insert={insert}
              remove={remove}
              countries={countries}
              fieldPath={`${fieldPrefix}.${FORM_FIELDS.IDENTIFYING_INFORMATION}`}
              isPartialMode={false}
              personUuid={beneficiary.uuid}
            />
          )}
          name={`${fieldPrefix}.${FORM_FIELDS.IDENTIFYING_INFORMATION}`}
        />

        <FieldArray
          render={({ insert, remove }) => (
            <Documents
              error={error}
              insert={insert}
              remove={remove}
              fieldPath={`${fieldPrefix}.${FORM_FIELDS.DOCUMENTS}`}
              clearBackendErrors={clearBackendErrors}
              isPartialMode={false}
            />
          )}
          name={`${fieldPrefix}.${FORM_FIELDS.DOCUMENTS}`}
        />

        <OptionallyVisible visible={Boolean(beneficiary.uuid) || beneficiaryIndex > 0}>
          <BeneficiaryActions>
            <RecordButton type="button" inside flat onClick={() => handleDeleteClick(beneficiaryIndex, beneficiary, removeBeneficiary)}>
              <IconRemove />
              {t(LABELS.BENEFICIARY.REMOVE)}
            </RecordButton>
          </BeneficiaryActions>
        </OptionallyVisible>

        <OptionallyVisible visible={isNotLastBeneficiary}>
          <Divider />
        </OptionallyVisible>
      </BeneficiarySection>
    );
  };

  return (
    <Dialog fullWidth maxWidth="xs" open={isOpen} scroll="body" disableEscapeKeyDown>
      <ModalRoot>
        <Formik
          key={JSON.stringify(sessionValues)}
          initialValues={sessionValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          validateOnMount={false}
        >
          {({ values, resetForm, errors }) => {
            const beneficiaries = values.beneficiaries || [];

            const handleResetData = () => {
              resetSessionForm();
              resetForm({ values: ASSOCIATED_PERSONS_INITIAL_VALUES });
            };

            return (
              <Panel
                label={t(LABELS.MODAL.TITLE)}
                controls={
                  <FormControls
                    closeModal={closeModal}
                    resetData={handleResetData}
                    onInsertTitanData={onInsertTitanData}
                    titanFormData={titanFormData}
                    isLoadingTitanData={isLoadingTitanData}
                  />
                }
              >
                {isLoading ? (
                  <PanelContent>
                    <LoadingIndicator />
                  </PanelContent>
                ) : (
                  <BridgeKycInfoProvider value={bridgeKycInfo}>
                    <Form>
                      <FormikValuesObserver values={values} resetForm={resetForm} />
                      <PanelContent>
                        <FieldArray
                          name="beneficiaries"
                          render={({ push, remove }) => {
                            const handleAddBeneficiary = () => push({ ...ASSOCIATED_PERSON_INITIAL_VALUES });

                            return (
                              <>
                                {beneficiaries.map((beneficiary, index) =>
                                  renderBeneficiaryForm(index, beneficiary, beneficiaries.length, remove, errors),
                                )}

                                <RecordButton type="button" flat onClick={handleAddBeneficiary}>
                                  <IconAdd />
                                  {t(LABELS.BENEFICIARY.ADD)}
                                </RecordButton>
                              </>
                            );
                          }}
                        />

                        <Delimeter />
                        <Buttons>
                          <Button secondary onClick={closeModal}>
                            {t(LABELS.MODAL.CANCEL)}
                          </Button>
                          <Button primary onClick={() => {}} type="submit" disabled={isSubmitting}>
                            {t(LABELS.MODAL.CONFIRM)}
                          </Button>
                        </Buttons>
                      </PanelContent>
                    </Form>
                  </BridgeKycInfoProvider>
                )}
              </Panel>
            );
          }}
        </Formik>
      </ModalRoot>

      <DeleteConfirmationModal
        isOpen={deleteConfirmation.isOpen}
        isDeleting={isDeleting}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
      />
    </Dialog>
  );
};
